import { Combobox } from '@/shared/components/ui/combobox';
import { cn } from '@/shared/lib/cn';
import {
  CASAS_DOLAR,
  CASAS_DOLAR_LABEL,
  type CasaDolar,
  type VistaMoneda,
} from '@/shared/lib/tipoCambio';

/**
 * Los dos controles de tipo de cambio del Estado Financiero, compartidos con
 * Tesorería (pedido del cliente 2026-09-04: "el toggle que tenemos en el
 * dashboard"). Viven acá y no duplicados en cada pantalla para que las
 * etiquetas de las casas no se bifurquen: dos listas que dicen distinto para
 * la misma cotización es cómo se pierde la confianza en los dos tableros.
 *
 * Sólo presentación — cada pantalla decide qué convierte y dónde.
 */

/**
 * Moneda de la vista. Es una CONVERSIÓN: las dos opciones miran los mismos
 * datos y sólo cambian la unidad. Los totales de una y otra reconcilian.
 */
export function MonedaToggle({
  valor,
  monedaLocal,
  onChange,
  titleLocal,
  titleUsd,
}: {
  valor: VistaMoneda;
  monedaLocal: string;
  onChange: (v: VistaMoneda) => void;
  titleLocal?: string;
  titleUsd?: string;
}) {
  const opciones: ReadonlyArray<{ id: VistaMoneda; label: string; title: string }> = [
    {
      id: 'local',
      label: monedaLocal,
      title:
        titleLocal ?? `Todo el flujo en ${monedaLocal}; los movimientos en dólares se convierten`,
    },
    {
      id: 'usd',
      label: 'USD',
      title: titleUsd ?? 'Todo el flujo en dólares, al oficial de la fecha de cada movimiento',
    },
  ];
  const activo: VistaMoneda = valor === 'usd' ? 'usd' : 'local';
  return (
    <div
      className="flex h-12 shrink-0 items-center gap-1"
      role="group"
      aria-label="Moneda de la vista"
    >
      {opciones.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          aria-pressed={activo === o.id}
          title={o.title}
          className={cn(
            'rounded-md px-3 py-2 text-sm font-medium transition-colors',
            activo === o.id
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const CASA_ITEMS = CASAS_DOLAR.map((id) => ({ id, label: CASAS_DOLAR_LABEL[id] }));

/**
 * Casa de cambio con la que se convierte.
 *
 * Va en un select y no en un segmentado como Moneda: son cinco opciones, se
 * elige una vez y no se alterna a cada rato.
 */
export function CasaSelector({
  valor,
  onChange,
  disabled,
}: {
  valor: CasaDolar;
  onChange: (c: CasaDolar) => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex h-12 items-center gap-2"
      title="Serie diaria con la que se convierte"
    >
      <span className="text-xs text-muted-foreground">Cotización</span>
      <Combobox
        value={CASA_ITEMS.find((c) => c.id === valor) ?? null}
        onChange={(c) => c && onChange(c.id)}
        items={CASA_ITEMS}
        getKey={(c) => c.id}
        getLabel={(c) => c.label}
        matcher={(c, q) => c.label.toLowerCase().includes(q)}
        className="h-12 w-36"
        disabled={disabled}
      />
    </div>
  );
}
